import {
  Dialog,
  DialogContent,
  InputAdornment,
  Skeleton,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchHelp from "components/SearchField/SearchHelp";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import { format } from "date-fns";
import {
  FunctionComponent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiGlobalSearch } from "util/network/Search";
import { API_URL } from "util/network/common";
import { GlobalSearchResultsType, SearchHit } from "types/SearchTypes";
import styles from "./searchResultsModal.module.css";

type SectionType = {
  title: string;
  hits: SearchHit[];
  startIndex: number;
  // Matches before the server's per-group cap, and where "See all" goes.
  total: number;
  seeAllPath: string;
  // Label for the "See all" button. Carries the count only where the
  // destination really lists that many rows, see the Parts section below.
  seeAllLabel: string;
};

type PropsType = {
  open: boolean;
  initialQuery: string;
  token: string;
  // A hit was picked (click, or Enter on a focused row). Caller is expected
  // to close the modal (open -> false) and defer any navigation/side effects
  // until onExited fires, see GlobalSearch.tsx.
  onSelectHit: (hit: SearchHit) => void;
  // "See all N" was clicked - caller closes the modal and routes there.
  onSeeAll: (path: string) => void;
  // Esc key or backdrop click - no hit was picked.
  onRequestClose: () => void;
  // Fired once the close transition has fully finished and the dialog has
  // left the DOM.
  onExited: () => void;
};

const SKELETON_ROWS = 6;

// A single character matches "%a%" against every searchable column and
// comes back with an arbitrary handful of rows - noise, not results.
// "#1" is a legitimate exact-id lookup, so hash queries are exempt.
const MIN_QUERY_LENGTH = 2;

const isSearchable = (trimmed: string) =>
  trimmed.startsWith("#") || trimmed.length >= MIN_QUERY_LENGTH;

// The created date the server sends is `YYYY-MM-DDTHH:mm:ss`; the ticket
// asks for dd-MM-YYYY. That rules out util/DateUtil's reformatDate, which
// renders en-GB as dd/MM/yyyy with slashes.
//
// Returns null rather than a string for anything it cannot render, and the
// row then shows no date at all. Three ways that happens, all real: the
// column is nullable on legacy/Magento-imported rows; a backend that
// predates EE-222's `created` omits the field entirely (staging still runs
// an older image); and `new Date()` on anything unexpected yields an
// Invalid Date, which date-fns' format() throws on. "Invalid Date" in a
// result row is worse than no date.
const formatCreated = (created: string | null | undefined): string | null => {
  if (!created) return null;
  const parsed = new Date(created);
  if (Number.isNaN(parsed.getTime())) return null;
  return format(parsed, "dd-MM-yyyy");
};

const SearchResultsModal: FunctionComponent<PropsType> = ({
  open,
  initialQuery,
  token,
  onSelectHit,
  onSeeAll,
  onRequestClose,
  onExited,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [query, setQuery] = useState<string>(initialQuery);
  const [results, setResults] = useState<GlobalSearchResultsType | null>(null);
  // The trimmed query `results` actually came from. `query` keeps changing as
  // the user edits the box, so it is the wrong thing to build "See all" links
  // (or the empty-state message) from - those belong to the result set on
  // screen, not to a term nobody has searched for yet.
  const [resultsQuery, setResultsQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const requestIdRef = useRef<number>(0);

  const runSearch = (rawQuery: string) => {
    const trimmed = rawQuery.trim();
    const requestId = ++requestIdRef.current;
    setFocusedIndex(null);

    if (!isSearchable(trimmed)) {
      setResults(null);
      setLoading(false);
      setError(
        trimmed
          ? `Type at least ${MIN_QUERY_LENGTH} characters to search`
          : null
      );
      return;
    }

    setLoading(true);
    setError(null);
    apiGlobalSearch(token, trimmed)
      .then((res) => {
        if (requestIdRef.current !== requestId) return;
        setResults(res);
        setResultsQuery(trimmed);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        console.error("Global search failed:", err);
        setResults(null);
        setError("Search failed. Please try again.");
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      });
  };

  // Seed the modal's own editable query field and fire the initial search
  // every time it transitions from closed to open.
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  const sections: SectionType[] = useMemo(() => {
    if (!results) return [];
    const list: SectionType[] = [];
    let index = 0;
    // ?search= seeds the destination list page's own search box; those
    // pages keep their term in session storage, so a bare link could not
    // otherwise hand them one. Parts have no list page of their own - the
    // order table's search matches ORDERITEMS.name with the same rules, so
    // a part search resolves there.
    const encoded = encodeURIComponent(resultsQuery);
    if (results.orders.length > 0) {
      list.push({
        title: "Orders",
        hits: results.orders,
        startIndex: index,
        total: results.ordersTotal,
        seeAllPath: `/ordrestyring?search=${encoded}`,
        seeAllLabel: `See all ${results.ordersTotal}`,
      });
      index += results.orders.length;
    }
    if (results.parts.length > 0) {
      list.push({
        title: "Parts",
        hits: results.parts,
        startIndex: index,
        total: results.partsTotal,
        seeAllPath: `/ordrestyring?search=${encoded}`,
        // No count here: partsTotal counts matching ORDERITEMS rows, but the
        // link lands on the order table, which lists one row per order and
        // matches on order columns too. Neither number describes the other,
        // so promising "See all 23" and then showing a different count is
        // worse than not promising one.
        seeAllLabel: "See all in orders",
      });
      index += results.parts.length;
    }
    if (results.customers.length > 0) {
      list.push({
        title: "Customers",
        hits: results.customers,
        startIndex: index,
        total: results.customersTotal,
        seeAllPath: `/kunder?search=${encoded}`,
        seeAllLabel: `See all ${results.customersTotal}`,
      });
      index += results.customers.length;
    }
    return list;
  }, [results, resultsQuery]);

  const flatHits: SearchHit[] = useMemo(
    () => sections.flatMap((section) => section.hits),
    [sections]
  );

  // Keep the highlighted row in view while navigating with the arrow keys.
  useEffect(() => {
    if (focusedIndex === null) return;
    document
      .getElementById(`global-search-hit-${focusedIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (focusedIndex !== null && flatHits[focusedIndex]) {
        onSelectHit(flatHits[focusedIndex]);
      } else {
        runSearch(query);
      }
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (flatHits.length === 0) return;
      event.preventDefault();
      setFocusedIndex((prev) => {
        const base = prev ?? -1;
        const next = event.key === "ArrowDown" ? base + 1 : base - 1;
        return Math.max(0, Math.min(flatHits.length - 1, next));
      });
    }
    // Escape is left to the Dialog itself (onClose below) so there's a
    // single source of truth for "closed without a selection".
  };

  const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, i) => (
    <div key={i} className={styles.skeletonRow}>
      <Skeleton variant="rectangular" width={40} height={40} />
      <div className={styles.skeletonText}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="35%" />
      </div>
    </div>
  ));

  return (
    <Dialog
      open={open}
      onClose={onRequestClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      TransitionProps={{ onExited }}
      PaperProps={{ className: styles.paper }}
    >
      <DialogContent className={styles.content}>
        <div className={styles.searchRow}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // The highlight belongs to the result set the old query
              // produced. Without this, refining a query while a row is
              // focused makes the next Enter navigate to that stale hit
              // instead of re-searching.
              setFocusedIndex(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search orders, parts, customers..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <SearchHelp variant="global" />
        </div>

        <div className={styles.results}>
          {loading ? (
            <div className={styles.skeletonList}>{skeletonRows}</div>
          ) : error ? (
            <div className={styles.emptyState}>{error}</div>
          ) : !results || flatHits.length === 0 ? (
            <div className={styles.emptyState}>
              {results
                ? `No results found for "${resultsQuery}"`
                : "Type a search term and press Enter"}
            </div>
          ) : (
            sections.map((section) => (
              <div className={styles.section} key={section.title}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionTitle}>{section.title}</span>
                  <span className={styles.sectionCount}>
                    {section.total > section.hits.length
                      ? `${section.hits.length} of ${section.total}`
                      : section.hits.length}
                  </span>
                  {section.total > section.hits.length && (
                    <button
                      type="button"
                      className={styles.seeAll}
                      onClick={() => onSeeAll(section.seeAllPath)}
                    >
                      {section.seeAllLabel}
                    </button>
                  )}
                </div>
                <ul className={styles.list}>
                  {section.hits.map((hit, i) => {
                    const flatIndex = section.startIndex + i;
                    const created = formatCreated(hit.created);
                    return (
                      <li
                        id={`global-search-hit-${flatIndex}`}
                        key={`${hit.type}-${hit.id}`}
                        className={`${styles.row} ${
                          flatIndex === focusedIndex ? styles.rowFocused : ""
                        }`}
                        onClick={() => onSelectHit(hit)}
                      >
                        {hit.type === "part" && (
                          <div className={styles.thumb}>
                            {hit.modelId != null ? (
                              <img
                                className={styles.thumbImg}
                                src={`${API_URL}/v1/ecommerce/models/${hit.modelId}/preview`}
                                alt=""
                              />
                            ) : (
                              <ImageNotSupportedOutlinedIcon
                                fontSize="small"
                                className={styles.thumbPlaceholderIcon}
                              />
                            )}
                          </div>
                        )}
                        <div className={styles.rowText}>
                          <div className={styles.rowLabel}>{hit.label}</div>
                          <div className={styles.rowSublabel}>
                            {hit.sublabel}
                          </div>
                        </div>
                        {/* Right-aligned metadata column rather than a third
                            line under the sublabel: the row keeps its height,
                            and these are identifiers you scan down rather than
                            read. Product id is part-only - orders and
                            customers have no such concept. Either half may be
                            absent, so the column renders only when it has
                            something in it. */}
                        {(hit.type === "part" || created) && (
                          <div className={styles.rowMeta}>
                            {hit.type === "part" && (
                              <span
                                className={styles.rowProductId}
                                title="Product ID"
                              >
                                #{hit.productId}
                              </span>
                            )}
                            {created && (
                              <span
                                className={styles.rowCreated}
                                title="Created"
                              >
                                {created}
                              </span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchResultsModal;
